<app-page size="narrow">
    <div class="crumb-row">
        <app-breadcrumbs [crumbs]="crumbs ?? []"></app-breadcrumbs>
        @if (form()?.get('id').value) {
            <button (click)="deleteUser($event)" class="delete-button" mat-stroked-button type="button">
                <i class="fa-regular fa-trash-can"></i> Gebruiker verwijderen
            </button>
        }
    </div>

    @if (form()) {
        <form (ngSubmit)="save()" [formGroup]="form()" class="sections" id="user-form">
            <!-- Keeps Enter in a field saving the form, like the old submit button did -->
            <button [disabled]="form().invalid || saving" aria-hidden="true" class="d-none" tabindex="-1" type="submit"></button>

            <app-section description="Hoe de gebruiker heet en waarmee hij inlogt." icon="user" title="Account">
                <app-field for="name" label="Naam">
                    <mat-form-field>
                        <input formControlName="name" id="name" matInput name="name" placeholder="Bijv. Stijn Dusseldorp" type="text">
                        <mat-hint>Zichtbaar in Daily, Weekly en rapportages.</mat-hint>
                        <mat-error [control]="form().get('name')" customMessage="Dit veld moet minimaal 3 tekens bevatten"></mat-error>
                    </mat-form-field>
                </app-field>
                <!-- TODO(design): the design warns "Dit e-mailadres is al in gebruik."; neither the frontend nor the API checks for duplicate e-mail addresses. -->
                <app-field for="email" label="E-mailadres">
                    <mat-form-field>
                        <input formControlName="email" id="email" matInput name="email" placeholder="naam@codalt.com" type="email">
                        <mat-hint>Hiermee logt de gebruiker in.</mat-hint>
                        <mat-error [control]="form().get('email')" customMessage="Vul een correct e-mailadres in"></mat-error>
                    </mat-form-field>
                </app-field>
            </app-section>

            <app-section icon="shield" title="Rollen">
                <p class="intro-text" intro>
                    Bepalen wat de gebruiker mag zien en doen. Wat een rol precies mag, stel je in bij de
                    @if (!environment.production && !environment.test) {
                        <a [routerLink]="['/', Routenames.rightsMatrix]">rechtenmatrix</a>.
                    } @else {
                        rechtenmatrix.
                    }
                </p>
                <app-field full label="Rollen">
                    <ng-select [items]="roles" [multiple]="true" [searchable]="true" bindLabel="name" bindValue="key" formControlName="roles">
                    </ng-select>
                    @if (!form().get('roles').value?.length) {
                        <span class="ui-warn" hint>Zonder rol kan de gebruiker inloggen, maar niets zien.</span>
                    }
                </app-field>
                <app-user-role-descriptions [roles]="roles" [selected]="form().get('roles').value" class="full-row"></app-user-role-descriptions>
            </app-section>

            <app-section description="Bepaalt het contract per week en de periode waarover totalen en saldo worden berekend." icon="calendar" title="Dienstverband">
                <app-field for="employment_start" label="In dienst per">
                    <mat-form-field>
                        <input
                            (focus)="pickerStart.open()"
                            [matDatepicker]="pickerStart"
                            [min]="minDate"
                            formControlName="employment_start"
                            id="employment_start"
                            matInput>
                        <mat-datepicker-toggle [for]="pickerStart" matSuffix></mat-datepicker-toggle>
                        <mat-datepicker #pickerStart></mat-datepicker>
                        <mat-hint>Startpunt voor profieltotalen.</mat-hint>
                        <mat-error [control]="form().get('employment_start')"></mat-error>
                    </mat-form-field>
                </app-field>
                <app-field for="employment_end" label="Uit dienst" optional>
                    <mat-form-field>
                        <input
                            (focus)="pickerEnd.open()"
                            [matDatepicker]="pickerEnd"
                            [min]="minDate"
                            formControlName="employment_end"
                            id="employment_end"
                            matInput>
                        <mat-datepicker-toggle [for]="pickerEnd" matSuffix></mat-datepicker-toggle>
                        <mat-datepicker #pickerEnd></mat-datepicker>
                        <mat-hint>Leeg laten = nog in dienst.</mat-hint>
                        <mat-error [control]="form().get('employment_end')"></mat-error>
                    </mat-form-field>
                </app-field>

                <app-field formArrayName="assignments" full label="Parttime percentage per periode">
                    <span class="ui-help periods-help">Bij een wijziging in het contract voeg je een nieuwe periode toe. Zo blijven de totalen over eerdere maanden kloppen.</span>
                    <div class="periods">
                        <div class="periods-inner">
                            <div class="period-row period-head">
                                <span>Van</span>
                                <span>Tot en met</span>
                                <span>Percentage</span>
                                <span>Per week</span>
                                <span></span>
                            </div>
                            @for (assignment of assignments.controls; track assignment; let i = $index) {
                                <div [class.current]="isCurrentPeriod(assignment.value)" [formGroupName]="i" class="period-row">
                                    <mat-form-field>
                                        <input
                                            (focus)="pickerFrom.open()"
                                            [matDatepicker]="pickerFrom"
                                            aria-label="Van"
                                            formControlName="begin_date"
                                            matInput>
                                        <mat-datepicker-toggle [for]="pickerFrom" matSuffix></mat-datepicker-toggle>
                                        <mat-datepicker #pickerFrom></mat-datepicker>
                                        <mat-error [control]="assignment.get('begin_date')"></mat-error>
                                    </mat-form-field>
                                    <mat-form-field>
                                        <input
                                            (focus)="pickerTo.open()"
                                            [matDatepicker]="pickerTo"
                                            aria-label="Tot en met"
                                            formControlName="end_date"
                                            matInput>
                                        <mat-datepicker-toggle [for]="pickerTo" matSuffix></mat-datepicker-toggle>
                                        <mat-datepicker #pickerTo></mat-datepicker>
                                        @if (!assignment.value.end_date) {
                                            <mat-hint>Leeg = lopend</mat-hint>
                                        }
                                    </mat-form-field>
                                    <mat-form-field>
                                        <input
                                            aria-label="Percentage"
                                            class="font-monospace"
                                            formControlName="part_time_rate"
                                            matInput
                                            max="100"
                                            min="0"
                                            type="number">
                                        <span class="ui-faint" matTextSuffix>%</span>
                                        <mat-error [control]="assignment.get('part_time_rate')"></mat-error>
                                    </mat-form-field>
                                    <span class="per-week">
                                        @let rate = assignment.value.part_time_rate;
                                        @if (rate > 0 && rate <= 100) {
                                            <span class="ui-mono">{{ rate * 0.4 | number:'1.0-1' }} u</span>
                                        } @else {
                                            <span class="ui-faint">—</span>
                                        }
                                        @if (isCurrentPeriod(assignment.value)) {
                                            <app-status-badge [dot]="false" size="sm" tone="ok">Huidig</app-status-badge>
                                        }
                                    </span>
                                    <button (click)="removeAssignment(i)" aria-label="Periode verwijderen" class="remove-period" mat-icon-button
                                            title="Periode verwijderen" type="button">
                                        <i class="fa-regular fa-trash-can"></i>
                                    </button>
                                </div>
                            } @empty {
                                <div class="periods-empty">Nog geen periodes. Zonder periode telt de gebruiker niet mee in de totalen.</div>
                            }
                            @if (assignments.hasError('endBeforeBegin')) {
                                <div class="periods-warning">Een periode kan niet eindigen voordat hij begint</div>
                            }
                            @if (assignments.hasError('overlap')) {
                                <div class="periods-warning">De periodes mogen elkaar niet overlappen, geef de vorige periode een einddatum</div>
                            }
                        </div>
                        <button (click)="addAssignment()" class="add-period" mat-button type="button">
                            <i class="fa-regular fa-plus"></i> Periode toevoegen
                        </button>
                    </div>
                </app-field>
            </app-section>

            <!-- TODO(design): README open point 12 — who may see the cost fields is not confirmed; visibility is unchanged (always shown on this page). -->
            <app-section description="Gebruikt in Financials. Niet zichtbaar voor de gebruiker zelf." icon="euro-sign" title="Kosten">
                <app-field for="hourly_rate" label="Tarief per uur">
                    <mat-form-field>
                        <span class="ui-faint" matTextPrefix>€&nbsp;</span>
                        <input
                            class="font-monospace"
                            formControlName="hourly_rate"
                            id="hourly_rate"
                            matInput
                            name="hourly_rate"
                            placeholder="0,00"
                            step="0.01"
                            type="number">
                        <mat-hint>Interne kostprijs per gewerkt uur.</mat-hint>
                    </mat-form-field>
                </app-field>
                <app-field for="period_costs" label="Periodieke kosten" optional>
                    <mat-form-field>
                        <span class="ui-faint" matTextPrefix>€&nbsp;</span>
                        <input
                            class="font-monospace"
                            formControlName="period_costs"
                            id="period_costs"
                            inputmode="decimal"
                            matInput
                            name="period_costs"
                            placeholder="0,00"
                            type="text">
                        <span class="ui-faint suffix" matTextSuffix>per periode</span>
                        <mat-hint>Vaste kosten zoals laptop of lease.</mat-hint>
                    </mat-form-field>
                </app-field>
            </app-section>
        </form>

        <app-sticky-save-bar
            (cancel)="cancel()"
            (save)="save()"
            [dirty]="form().dirty"
            [saveLabel]="form().get('id').value ? 'Opslaan' : 'Gebruiker aanmaken'"
            [saving]="saving"
            [valid]="form().valid">
        </app-sticky-save-bar>
    }
</app-page>
